// src/services/storeImageService.ts
import { supabase } from '../../lib/supabase';
import { Platform } from 'react-native';
import { uploadAsync, FileSystemUploadType } from 'expo-file-system/legacy';

const BUCKET = 'vendor-images';

/**
 * Uploads an image using fetch (works on web and native)
 */
async function uploadWithFetch(
    uploadUrl: string,
    blob: Blob,
    accessToken: string,
    contentType: string
): Promise<Response> {
    return fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': contentType,
            'x-upsert': 'true',
        },
        body: blob,
    });
}

/**
 * Converts a local URI to a Blob for web upload
 */
async function uriToBlob(uri: string): Promise<Blob> {
    const response = await fetch(uri);
    return response.blob();
}

/**
 * Uploads a local image URI to your Supabase "store-images" bucket.
 * @param uri       local file URI
 * @param vendorId  your vendor identifier
 * @returns         a public URL to the uploaded file
 */
export async function uploadProductImage(
    uri: string,
    vendorId: string,
): Promise<string> {
    // figure out extension
    const fileExt = uri.split('.').pop()?.toLowerCase() ?? 'jpg';
    // put it in a vendor-specific folder with a timestamp
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `vendor-images/${vendorId}/${fileName}`;

    // construct the raw upload endpoint
    const uploadUrl = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/${BUCKET}/${filePath}`;

    // grab your auth token
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
        throw new Error('No user session—cannot upload.');
    }

    const contentType = `image/${fileExt}`;

    if (Platform.OS === 'web') {
        // Web: convert URI to blob and use fetch
        const blob = await uriToBlob(uri);
        const res = await uploadWithFetch(uploadUrl, blob, accessToken, contentType);

        if (!res.ok) {
            throw new Error(`Upload failed with status ${res.status}`);
        }
    } else {
        // Native (iOS/Android): use expo-file-system uploadAsync
        const res = await uploadAsync(uploadUrl, uri, {
            httpMethod: 'PUT',
            uploadType: FileSystemUploadType.BINARY_CONTENT,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': contentType,
                'x-upsert': 'true',
            },
        });

        if (res.status !== 200) {
            throw new Error(`Upload failed with status ${res.status}`);
        }
    }

    // construct and return the public URL
    return `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${filePath}`;
}

/**
 * Deletes an image from your "store-images" bucket.
 * @param imageUrl   the full public URL returned by uploadStoreImage()
 */
export async function deleteProductImage(imageUrl: string): Promise<void> {
    // extract the path after "/store-images/"
    const parts = imageUrl.split(`/${BUCKET}/`);
    const path = parts[1];
    if (!path) {
        console.warn('Could not extract path from URL', imageUrl);
        return;
    }

    const { error } = await supabase.storage
        .from(BUCKET)
        .remove([path]);

    if (error) {
        console.warn('Error removing store image:', error.message);
    }
}